import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import {
  ClipboardList,
  FileCheck2,
  PackageCheck,
  Receipt,
  RotateCcw,
  Send,
  Truck,
  Wrench,
  ChevronRight,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { OperationRecord, OperationRecordsByTab, OperationSummary, OperationTab } from "../types";

const cards = [
  { key: "requests", label: "Stock Requests", note: "Requests raised", icon: ClipboardList, color: "#2563eb" },
  { key: "transfers", label: "Transfers", note: "Hub movements", icon: Truck, color: "#4f46e5" },
  { key: "receipts", label: "Goods Receipts", note: "Inbound stock", icon: PackageCheck, color: "#059669" },
  { key: "issues", label: "Spare Issues", note: "Issued to service", icon: Send, color: "#7c3aed" },
  { key: "returns", label: "Spare Returns", note: "Returned to hub", icon: RotateCcw, color: "#0284c7" },
  { key: "spareUsage", label: "Service Usage", note: "Consumed in service", icon: Wrench, color: "#9333ea" },
  { key: "invoices", label: "Invoices", note: "Service costs", icon: Receipt, color: "#0891b2" },
  { key: "pendingActions", label: "Pending Actions", note: "Needs attention", icon: FileCheck2, color: "#d97706" },
] as const;

interface OperationsSummaryCardsProps {
  summary: OperationSummary;
  records: OperationRecordsByTab;
}

const getTrend = (records: OperationRecord[]) => {
  const now = Date.now();
  const period = 30 * 24 * 60 * 60 * 1000;
  const recentStart = now - period;
  const previousStart = now - period * 2;
  const datedRecords = records
    .map((record) => new Date(record.createdAt || record.updatedAt || "").getTime())
    .filter((value) => Number.isFinite(value));
  const recent = datedRecords.filter((value) => value >= recentStart && value <= now).length;
  const previous = datedRecords.filter((value) => value >= previousStart && value < recentStart).length;

  if (!recent && !previous) return { label: "No recent activity", tone: "neutral" as const };
  if (!previous) return { label: "New this period", tone: "up" as const };

  const percentage = Math.round(((recent - previous) / previous) * 100);
  return percentage >= 0
    ? { label: `+${percentage}% vs prior 30 days`, tone: "up" as const }
    : { label: `${percentage}% vs prior 30 days`, tone: "down" as const };
};

const OperationsSummaryCards = ({ summary, records }: OperationsSummaryCardsProps) => (
  <Grid container spacing={1}>
    {cards.map(({ key, label, note, icon: Icon, color }) => (
      <Grid item xs={12} sm={6} md={3} key={key}>
        <Paper
          elevation={0}
          sx={{
            p: 1,
            border: "1px solid",
            borderColor: `${color}22`,
            borderRadius: 2,
            bgcolor: "background.paper",
            minHeight: 70,
            position: "relative",
            overflow: "hidden",
            transition: "transform 180ms ease, box-shadow 180ms ease",
            "&:hover": { transform: "translateY(-2px)", boxShadow: `0 12px 24px ${color}18` },
            "&:before": { content: '\"\"', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: color },
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
            <BoxIcon color={color}>
              <Icon size={20} />
            </BoxIcon>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                {label}
              </Typography>
              <Typography variant="h6" fontWeight={850} sx={{ mt: 0, lineHeight: 1.15 }}>
                {summary[key]}
              </Typography>
              {key === "pendingActions" ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>{note}</Typography>
              ) : (
                <Trend records={records[key as OperationTab]} color={color} fallback={note} />
              )}
            </Stack>
            <Box sx={{ display: "grid", placeItems: "center", width: 26, height: 26, borderRadius: "50%", bgcolor: `${color}10`, color }}><ChevronRight size={14} /></Box>
          </Stack>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

const Trend = ({ records, color, fallback }: { records: OperationRecord[]; color: string; fallback: string }) => {
  const trend = getTrend(records);
  const Icon = trend.tone === "up" ? TrendingUp : trend.tone === "down" ? TrendingDown : null;
  const tone = trend.tone === "down" ? "error.main" : trend.tone === "up" ? "success.main" : "text.secondary";
  return <Stack direction="row" spacing={.4} alignItems="center"><Box sx={{ color: tone, display: "grid" }}>{Icon && <Icon size={13} />}</Box><Typography variant="caption" sx={{ color: tone, fontWeight: trend.tone === "neutral" ? 400 : 800 }}>{trend.label || fallback}</Typography></Stack>;
};

const BoxIcon = ({ children, color }: { children: ReactNode; color: string }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{
      width: 32,
      height: 32,
      borderRadius: 1.5,
      color,
      bgcolor: `${color}12`,
    }}
  >
    {children}
  </Stack>
);

export default OperationsSummaryCards;
